import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext.tsx';

interface AlumniData {
  id: string;
  firstName: string;
  lastName: string;
  category: string;
  email: string;
  phone: string;
  textOptIn: boolean;
  isActingBigSister: boolean;
  isActive: boolean;
  notes?: string;
}

export default function AlumniProfile() {
  const { user } = useAuth();
  const [alumniData, setAlumniData] = useState<AlumniData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    textOptIn: false
  });
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAlumniData();
  }, []);

  const fetchAlumniData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/alumni/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setAlumniData(data.data);
        setFormData({
          email: data.data.email || '',
          phone: data.data.phone || '',
          textOptIn: data.data.textOptIn || false
        });
      }
    } catch (error) {
      console.error('Error fetching alumni data:', error);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
    setSaveSuccess(false);
    setError('');
  };

  const handleCancel = () => {
    setEditing(false);
    setFormData({
      email: alumniData?.email || '',
      phone: alumniData?.phone || '',
      textOptIn: alumniData?.textOptIn || false
    });
    setError('');
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/alumni/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        setAlumniData(data.data);
        setEditing(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'ranch-girl':
        return 'Alumni Ranch Girl';
      case 'alumni-mom':
        return 'Alumni Mom';
      case 'alumni-dad':
        return 'Alumni Dad';
      default:
        return category;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ggaa-green"></div>
      </div>
    );
  }

  if (!alumniData) {
    return (
      <div className="p-8">
        <div className="text-center text-red-600">
          Failed to load profile data
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-4 sm:py-6 lg:py-10">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Profile</h1>
        <p className="text-gray-600 mt-1">View and manage your alumni profile</p>
      </div>

      {/* Success Message */}
      {saveSuccess && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          ✓ Profile updated successfully
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Profile Card */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-ggaa-green to-ggaa-dark px-6 py-4">
          <h2 className="text-xl font-semibold text-white">Alumni Information</h2>
        </div>

        <div className="p-6 space-y-6">
          {/* Name (Read-only) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                {alumniData.firstName}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                {alumniData.lastName}
              </div>
            </div>
          </div>

          {/* Category (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
              {getCategoryLabel(alumniData.category)}
              {alumniData.isActingBigSister && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Big Sister
                </span>
              )}
            </div>
          </div>

          {/* Email (Editable) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            {editing ? (
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
              />
            ) : (
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                {alumniData.email}
              </div>
            )}
          </div>

          {/* Phone (Editable) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            {editing ? (
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ggaa-green focus:border-transparent"
              />
            ) : (
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                {alumniData.phone || 'Not provided'}
              </div>
            )}
          </div>

          {/* Text Opt-In (Editable) */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={editing ? formData.textOptIn : alumniData.textOptIn}
                onChange={(e) => editing && setFormData({ ...formData, textOptIn: e.target.checked })}
                disabled={!editing}
                className="h-5 w-5 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
              />
              <div>
                <span className="block font-semibold text-gray-900">📱 Opt in for Text Notifications</span>
                <span className="block text-sm text-gray-600">Receive important updates via SMS</span>
              </div>
            </label>
          </div>

          {/* Notes (Read-only) */}
          {alumniData.notes && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900 whitespace-pre-wrap">
                {alumniData.notes}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            {editing ? (
              <>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-dark transition-colors"
                >
                  Save Changes
                </button>
              </>
            ) : (
              <button
                onClick={handleEdit}
                className="px-4 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-dark transition-colors"
              >
                Edit Profile
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

